import { useEffect, useMemo, useState } from "react";
import { MobileBottomTabBar, MobileTopHeader } from "../MobileAppShell";
import { ConfiguredPageDisabledState, ConfiguredPageIntro } from "../AppConfig/ConfiguredPageState";
import { REGION_LIST } from "../../Common/ts/const";
import { APP_PAGE_KEYS, getSectionProps, getVisibleSections } from "../../Common/appConfig/appConfig";
import { useAppPageConfig } from "../../Common/hooks/useAppConfig";
import {
  useMystudioThemesV4,
  useSubmitThemeSelectionV4,
  useToggleThemeFavoriteV4,
} from "../../Common/api/v4";
import MystudioDataState from "../MystudioDataState";
import "./ShootingThemePage.css";

const TEXT = {
  title: "\uD14C\uB9C8",
  all: "\uC804\uCCB4",
  family: "\uAC00\uC871\uC0AC\uC9C4",
  maternity: "\uB9CC\uC0AD\uC0AC\uC9C4",
  day50: "50\uC77C",
  day100: "100\uC77C",
  firstBirthday: "\uB3CC\uC0AC\uC9C4",
  etc: "\uAE30\uD0C0",
  allOffice: "\uC804\uCCB4\uC9C0\uC810",
  fallbackOffice: "\uBD84\uB2F9\uC810",
  fallbackTitle: "\uD648 \uC2A4\uB0C5 \uB290\uB08C\uC758 \uC790\uC5F0\uC2A4\uB7EC\uC6B4 \uC5F0\uCD9C",
  fallbackDate: "2026.06.06",
  loading: "\uD14C\uB9C8\uB97C \uBD88\uB7EC\uC624\uB294 \uC911\uC785\uB2C8\uB2E4.",
  empty: "\uD45C\uC2DC\uD560 \uCD2C\uC601 \uD14C\uB9C8\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4.",
  preparing: "\uD14C\uB9C8 \uC0C1\uC138 \uD654\uBA74\uC740 \uC900\uBE44 \uC911\uC785\uB2C8\uB2E4.",
  detailInfo: "\uC0C1\uC138\uC815\uBCF4",
  popularCopy: "24\uC8FC\uCC28 \uC5C4\uB9C8\uB4E4\uC774 \uAC00\uC7A5 \uB9CE\uC774 \uC120\uD0DD\uD55C \uC81C\uD488\uC774\uC5D0\uC694",
  like: "\uCC1C\uD558\uAE30",
  back: "\uB4A4\uB85C\uAC00\uAE30",
  selectModeTitle: "\uCD2C\uC601\uD14C\uB9C8 \uC120\uD0DD\uC911",
  selectModeHelp: "100\uC77C \uCD2C\uC601 2\uAC1C, \uB3CC \uCD2C\uC601 2\uAC1C\uB97C \uACE0\uB974\uBA74 \uCD2C\uC601\uC77C\uC815\uC5D0 \uBC14\uB85C \uC800\uC7A5\uB418\uC5B4\uC694.",
  selectComplete: "\uC120\uD0DD \uC644\uB8CC",
  selected: "\uC120\uD0DD\uB428",
  selectTheme: "\uC120\uD0DD\uD558\uAE30",
  returnSchedule: "\uCD2C\uC601\uC77C\uC815\uC73C\uB85C \uB3CC\uC544\uAC00\uAE30",
  favorite: "\uCC1C\uD55C \uD14C\uB9C8",
  favoriteSaved: "\uCC1C\uD55C \uD14C\uB9C8",
  favoriteRemove: "\uCC1C\uD55C \uD14C\uB9C8\uC5D0\uC11C \uC0AD\uC81C",
  favoriteAdded: "\uCC1C\uD55C \uD14C\uB9C8\uC5D0 \uCD94\uAC00\uD588\uC5B4\uC694.",
  favoriteRemoved: "\uCC1C\uD55C \uD14C\uB9C8\uC5D0\uC11C \uC0AD\uC81C\uD588\uC5B4\uC694.",
  favoriteEmptyTitle: "\uC544\uC9C1 \uCC1C\uD55C \uD14C\uB9C8\uAC00 \uC5C6\uC5B4\uC694.",
  favoriteEmptyDescription: "\uB9C8\uC74C\uC5D0 \uB4DC\uB294 \uD14C\uB9C8\uC758 \uD558\uD2B8\uB97C \uB20C\uB7EC \uC800\uC7A5\uD574\uBCF4\uC138\uC694.",
  home: "\uD648",
  schedule: "\uCD2C\uC601 \uC77C\uC815",
  product: "\uC81C\uD488 \uC2E0\uCCAD",
  theme: "\uCD2C\uC601 \uD14C\uB9C8",
  mypage: "\uB9C8\uC774\uD398\uC774\uC9C0",
};

const ASSETS = {
  bell: "/static/img/figma-local/7ac7b2d4-9e78-4d81-b9e3-938fc698af6a.svg",
  heart: "/static/img/figma-local/afd2ad4a-294b-4c64-bf7b-6b7e4898f4c2.svg",
  heartActive: "/static/img/figma-local/afd2ad4a-294b-4c64-bf7b-6b7e4898f4c2.svg",
  location: "/static/img/figma-local/1a8a67d8-fb06-4c1a-a90f-18c3b2927650.svg",
  chevron: "/static/img/figma-local/2585b683-4de9-4e3f-aea4-2deeaf87bb9c.svg",
  close: "/static/img/figma-local/87d22b0d-f9e6-4d9d-9ed0-6085258910c3.svg",
  detailHero: "/static/img/figma-local/96d91a3b-2db6-49c0-b714-2e9a7990c541.png",
  selectionComplete: "/static/img/themes/theme-selection-complete-check.svg",
};

const CATEGORIES = [
  { key: "all", label: TEXT.all },
  { key: "favorite", label: TEXT.favorite },
  { key: "family", label: TEXT.family, match: ["\uAC00\uC871", "family"] },
  { key: "maternity", label: TEXT.maternity, match: ["\uB9CC\uC0AD", "maternity"] },
  { key: "day50", label: TEXT.day50, match: ["50"] },
  { key: "day100", label: TEXT.day100, match: ["100"] },
  { key: "firstBirthday", label: TEXT.firstBirthday, match: ["\uB3CC"] },
  { key: "etc", label: TEXT.etc },
];

const THEME_TOAST_DURATION = 1800;

const DEFAULT_FILTER_TOKENS = [
  { key: "tone", label: TEXT.all },
  { key: "concept", label: TEXT.all },
  { key: "people", label: TEXT.all },
  { key: "style", label: TEXT.all },
  { key: "mood", label: TEXT.all },
];

const REGION_FILTERS = ["\uC11C\uC6B8", "\uACBD\uAE30", "\uC778\uCC9C", "\uB300\uAD6C", "\uAD11\uC8FC", "\uB300\uC804", "\uC6B8\uC0B0", "\uC138\uC885", "\uAC15\uC6D0", "\uCDA9\uBD81"];

const OFFICE_SHEET_TEXT = {
  title: "\uC9C0\uC810\uC120\uD0DD",
  close: "\uB2EB\uAE30",
  allOffice: "\uC804\uCCB4\uC9C0\uC810",
  region: "\uC9C0\uC5ED",
  reset: "\uCD08\uAE30\uD654",
  apply: "\uC801\uC6A9\uD558\uAE30",
  more: "\uC678",
};
const DEFAULT_REGION_VALUE = REGION_LIST?.[0]?.value || "";

function MobileStatusBar() {
  return (
    <div className="figma-theme-status" aria-hidden="true">
      <span>9:41</span>
      <span className="figma-theme-status__icons">
        <i />
        <i />
        <i />
      </span>
    </div>
  );
}

function ThemeHeader() {
  return (
    <header className="figma-theme-header" data-node-id="7038:14652">
      <h1 data-node-id="7038:14653">{TEXT.title}</h1>
      <button className="figma-theme-header__bell" type="button" data-node-id="7038:14654" aria-label="\uC54C\uB9BC" onClick={() => window.alert(TEXT.preparing)}>
        <img src={ASSETS.bell} alt="" aria-hidden="true" />
      </button>
    </header>
  );
}

function CategoryFilter({ value, onChange }) {
  return (
    <div className="figma-theme-category" data-node-id="7038:14502">
      <div className="figma-theme-category__track">
        {CATEGORIES.map((category) => (
          <button
            key={category.key}
            type="button"
            className={value === category.key ? "figma-theme-chip figma-theme-chip--active" : "figma-theme-chip"}
            onClick={() => onChange(category.key)}
          >
            {category.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function ActiveFilterChips({ filters, onRemove }) {
  return (
    <div className="figma-theme-filter-tags" data-node-id="7038:14532">
      {filters.map((filter) => (
        <button key={filter.key} type="button" className="figma-theme-filter-token" onClick={() => onRemove(filter.key)}>
          <span>{filter.label}</span>
          <img src={ASSETS.close} alt="" aria-hidden="true" />
        </button>
      ))}
    </div>
  );
}

function OfficeSelector({ officeName, onOpen, fixed = false }) {
  return (
    <div className="figma-theme-office" data-node-id="6993:11632">
      <button
        className={`figma-theme-office__button${fixed ? " is-fixed" : ""}`}
        type="button"
        data-node-id="6993:11692"
        onClick={fixed ? undefined : onOpen}
        disabled={fixed}
        aria-label={fixed ? `${officeName} 계약 지점` : `${officeName} 지점 선택`}
      >
        <span className="figma-theme-office__content">
          <img src={ASSETS.location} alt="" aria-hidden="true" />
          <span>{officeName}</span>
        </span>
        {!fixed ? <img className="figma-theme-office__chevron" src={ASSETS.chevron} alt="" aria-hidden="true" /> : null}
      </button>
    </div>
  );
}

function ThemeFilterPanel({ selectedCategory, onSelectCategory, officeName, onOpenOfficeSheet, filters, onRemoveFilter, selectionMode = false, fixedOffice = false }) {
  return (
    <section className="figma-theme-filter-panel" data-node-id="7038:14501">
      {!selectionMode && <CategoryFilter value={selectedCategory} onChange={onSelectCategory} />}
      <div className="figma-theme-filter-row" data-node-id="7038:14524">
        <OfficeSelector officeName={officeName} onOpen={onOpenOfficeSheet} fixed={fixedOffice} />
        <ActiveFilterChips filters={filters} onRemove={onRemoveFilter} />
      </div>
    </section>
  );
}

function ScheduleThemeSelectionPanel({
  activeTypeId,
  selection,
  selectTypes,
  requiredPerType,
  title = TEXT.selectModeTitle,
  description = "",
  submitting = false,
  onSelectType,
  onComplete,
}) {
  const totalSelected = selectTypes.reduce((total, type) => total + (selection[type.id]?.length || 0), 0);
  const requiredTotal = selectTypes.length * requiredPerType;
  const complete = selectTypes.every((type) => (selection[type.id]?.length || 0) === requiredPerType);
  const helpText = `${selectTypes.map((type) => `${type.title} ${requiredPerType}개`).join(", ")}를 고르면 촬영일정에 바로 저장되어요.`;

  return (
    <section className="figma-theme-schedule-select">
      <div className="figma-theme-schedule-select__copy">
        <strong>{title}</strong>
        <p>{description || helpText}</p>
      </div>
      <div className="figma-theme-schedule-select__tabs">
        {selectTypes.map((type) => {
          const selectedCount = selection[type.id]?.length || 0;
          return (
            <button
              key={type.id}
              type="button"
              className={activeTypeId === type.id ? "is-active" : ""}
              onClick={() => onSelectType(type.id)}
            >
              <span>{type.title}</span>
              <strong>{selectedCount}/{requiredPerType}</strong>
            </button>
          );
        })}
      </div>
      <button
        className="figma-theme-schedule-select__complete"
        type="button"
        onClick={onComplete}
        disabled={!complete || submitting}
        aria-busy={submitting}
      >
        <span>{TEXT.selectComplete}</span>
        <strong>{totalSelected}/{requiredTotal}</strong>
      </button>
    </section>
  );
}

export function ThemeSelectionCompleteDialog({ onConfirm }) {
  return (
    <div className="figma-theme-complete-layer" role="presentation">
      <section
        className="figma-theme-complete-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="figma-theme-complete-title"
        data-node-id="6975:11364"
      >
        <div className="figma-theme-complete-copy" data-node-id="6975:11365">
          <div className="figma-theme-complete-title" data-node-id="6975:11366">
            <span className="figma-theme-complete-icon" data-node-id="6975:11367" aria-hidden="true">
              <img src={ASSETS.selectionComplete} alt="" />
            </span>
            <h2 id="figma-theme-complete-title" data-node-id="6975:11368">테마선택 완료!</h2>
          </div>
          <p data-node-id="6975:11369">테마 변경은 당일 스튜디오에 문의해주세요</p>
        </div>
        <button type="button" data-node-id="6975:11370" onClick={onConfirm}>
          <span data-node-id="6975:11372">확인</span>
        </button>
      </section>
    </div>
  );
}


function normalizeOfficeOption(office, index) {
  return {
    id: Number(office?.office_id ?? index + 1),
    name: office?.office_name || "스튜디오 정보 없음",
  };
}

function getDefaultOfficeIds(officeOptions, selectedOfficeId) {
  if (selectedOfficeId) return [selectedOfficeId];
  return officeOptions.slice(0, 3).map((office) => office.id);
}

function OfficeLocationSheet({ open, officeOptions, draftOfficeIds, selectedRegion, showRegionFilter = true, onSelectRegion, onToggleOffice, onRemoveOffice, onReset, onApply, onClose }) {
  if (!open) return null;

  const selectedOffices = officeOptions.filter((office) => draftOfficeIds.includes(office.id));

  return (
    <div className="figma-theme-office-sheet-backdrop" role="presentation" onClick={onClose}>
      <section className="figma-theme-office-sheet" role="dialog" aria-modal="true" aria-labelledby="theme-office-sheet-title" data-node-id="7038:14894" onClick={(event) => event.stopPropagation()}>
        <header className="figma-theme-office-sheet__header" data-node-id="7038:14897">
          <h2 id="theme-office-sheet-title" data-node-id="7038:14900">{OFFICE_SHEET_TEXT.title}</h2>
          <button className="figma-theme-office-sheet__close" type="button" aria-label={OFFICE_SHEET_TEXT.close} onClick={onClose} data-node-id="7038:14901">
            <span aria-hidden="true">{"\u00d7"}</span>
          </button>
        </header>

        <div className="figma-theme-office-sheet__body" data-node-id="7038:14919">
          <div className="figma-theme-office-sheet__selected" data-node-id="7038:14920">
            {selectedOffices.length > 0 ? (
              selectedOffices.map((office) => (
                <button key={office.id} className="figma-theme-office-sheet__selected-chip" type="button" onClick={() => onRemoveOffice(office.id)}>
                  <span>{office.name}</span>
                  <span aria-hidden="true">{"\u00d7"}</span>
                </button>
              ))
            ) : (
              <span className="figma-theme-office-sheet__selected-empty">{OFFICE_SHEET_TEXT.allOffice}</span>
            )}
          </div>

          {showRegionFilter ? (
            <section className="figma-theme-office-sheet__section" data-node-id="7038:14934">
              <h3>{OFFICE_SHEET_TEXT.region}</h3>
              <div className="figma-theme-office-sheet__regions" data-node-id="7038:14937">
                {REGION_FILTERS.map((region) => (
                  <button key={region} type="button" className={selectedRegion === region ? "figma-theme-office-sheet__region figma-theme-office-sheet__region--active" : "figma-theme-office-sheet__region"} onClick={() => onSelectRegion(region)}>
                    {region}
                  </button>
                ))}
              </div>
            </section>
          ) : null}

          <section className="figma-theme-office-sheet__section" data-node-id="7038:14970">
            <h3>{showRegionFilter ? selectedRegion : OFFICE_SHEET_TEXT.allOffice}</h3>
            <div className="figma-theme-office-sheet__offices" data-node-id="7038:14973">
              {officeOptions.map((office) => {
                const active = draftOfficeIds.includes(office.id);
                return (
                  <button key={office.id} type="button" className={active ? "figma-theme-office-sheet__office figma-theme-office-sheet__office--active" : "figma-theme-office-sheet__office"} onClick={() => onToggleOffice(office.id)}>
                    {office.name}
                  </button>
                );
              })}
            </div>
          </section>
        </div>

        <footer className="figma-theme-office-sheet__footer" data-node-id="7038:14905">
          <button className="figma-theme-office-sheet__reset" type="button" onClick={onReset} data-node-id="7038:14912">
            <span aria-hidden="true">{"\u21bb"}</span>
            <span>{OFFICE_SHEET_TEXT.reset}</span>
          </button>
          <button className="figma-theme-office-sheet__apply" type="button" onClick={onApply} data-node-id="7038:14916">{OFFICE_SHEET_TEXT.apply}</button>
        </footer>
      </section>
    </div>
  );
}

function normalizeV4Theme(row) {
  return {
    id: String(row.id),
    officeIds: Array.isArray(row.officeIds) ? row.officeIds.map(String) : [],
    officeName: row.officeName || "",
    title: row.title,
    description: row.description || "",
    date: "",
    image: row.thumbnailUrl || row.images?.[0]?.url || "",
    photoName: row.tags?.join(" ") || "",
    tags: row.tags || [],
    category: row.category || "etc",
    liked: Boolean(row.isFavorite),
    selectable: row.selectable !== false,
    source: "erp",
  };
}

function matchesCategory(theme, categoryKey) {
  if (categoryKey === "all") return true;
  if (theme.category === categoryKey) return true;

  const category = CATEGORIES.find((item) => item.key === categoryKey);
  if (!category?.match) return theme.category === categoryKey;

  const haystack = `${theme.title || ""} ${theme.photoName || ""}`.toLowerCase();
  return category.match.some((keyword) => haystack.includes(keyword.toLowerCase()));
}

function createStoredTheme(theme) {
  return {
    id: theme.id,
    title: theme.title,
    image: theme.image,
    officeIds: theme.officeIds,
    officeName: theme.officeName,
    date: theme.date,
    category: theme.category,
    photoName: theme.photoName || "",
  };
}

function normalizeFavoriteTheme(item, index = 0) {
  return {
    id: String(item?.id ?? `favorite-${index}`),
    title: item?.title || "촬영 테마",
    image: item?.image || "",
    officeIds: Array.isArray(item?.officeIds) ? item.officeIds.map(String) : [],
    officeName: item?.officeName || "",
    date: item?.date || "",
    category: item?.category || "etc",
    photoName: item?.photoName || "",
  };
}

function createFavoriteMap(themes) {
  return themes.reduce((map, theme) => {
    map[theme.id] = true;
    return map;
  }, {});
}

function ThemeCard({ theme, liked, onToggleLike, onOpen, selectionMode = false, selected = false, selectionDisabled = false }) {
  const cardClassName = [
    "figma-theme-card",
    selectionMode ? "figma-theme-card--select-mode" : "",
    selected ? "figma-theme-card--selected" : "",
    selectionDisabled ? "figma-theme-card--disabled" : "",
  ].filter(Boolean).join(" ");

  return (
    <article className={cardClassName}>
      <button className="figma-theme-card__thumb" type="button" onClick={onOpen} aria-label={theme.title}>
        {theme.image ? <img src={theme.image} alt="" loading="lazy" decoding="async" /> : null}
        {selectionMode && <span className="figma-theme-card__select-badge">{selected ? TEXT.selected : TEXT.selectTheme}</span>}
      </button>
      <div className="figma-theme-card__body">
        <div className="figma-theme-card__meta-row">
          <span className="figma-theme-card__office">{theme.officeName}</span>
          <button className="figma-theme-card__heart" type="button" aria-label={selectionMode ? TEXT.selectTheme : "\uC88B\uC544\uC694"} aria-pressed={selectionMode ? selected : liked} onClick={onToggleLike}>
            <img src={liked ? ASSETS.heartActive : ASSETS.heart} alt="" aria-hidden="true" />
          </button>
        </div>
        <button className="figma-theme-card__title" type="button" onClick={onOpen}>
          {theme.title}
        </button>
        {theme.date ? <p className="figma-theme-card__date">{theme.date}</p> : null}
        {selectionMode && (
          <button className="figma-theme-card__select-button" type="button" onClick={onOpen} disabled={selectionDisabled && !selected}>
            {selected ? TEXT.selected : TEXT.selectTheme}
          </button>
        )}
      </div>
    </article>
  );
}

function ThemeEmptyState({ favorite = false, officeName = "" }) {
  if (!favorite) {
    return (
      <div className="figma-theme-empty figma-theme-empty--branch">
        <strong>{officeName ? `${officeName} 촬영테마를 준비 중이에요` : TEXT.empty}</strong>
        {officeName ? <span>스튜디오에서 ERP에 테마를 등록하면 자동으로 표시됩니다.</span> : null}
      </div>
    );
  }

  return (
    <div className="figma-theme-empty figma-theme-empty--favorites">
      <strong>{TEXT.favoriteEmptyTitle}</strong>
      <span>{TEXT.favoriteEmptyDescription}</span>
    </div>
  );
}

function HeartIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M12 20.2s-7-4.4-9.2-8.4C.9 8.4 2.8 4.5 6.6 4.5c2.1 0 3.5 1.1 4.4 2.4.9-1.3 2.3-2.4 4.4-2.4 3.8 0 5.7 3.9 3.8 7.3C19 15.8 12 20.2 12 20.2Z" />
    </svg>
  );
}

function BackIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M15 5 8 12l7 7" />
    </svg>
  );
}

function ThemeDetailPage({ theme, liked, feedbackMessage, onBack, onToggleLike }) {
  const heroImage = theme?.image || "";
  const tags = Array.isArray(theme?.tags) ? theme.tags : [];
  const favoriteLabel = liked ? TEXT.favoriteSaved : TEXT.like;
  const favoriteAriaLabel = liked ? TEXT.favoriteRemove : TEXT.like;

  return (
    <section className="figma-theme-page figma-theme-detail mobile-app-frame" data-node-id="6975:9955">
      <MobileStatusBar />
      <header className="figma-theme-detail__header" data-node-id="6975:10496">
        <button className="figma-theme-detail__back" type="button" aria-label={TEXT.back} onClick={onBack}>
          <BackIcon />
        </button>
        <button className="figma-theme-detail__favorite-top" type="button" aria-label={favoriteAriaLabel} aria-pressed={liked} onClick={onToggleLike}>
          <HeartIcon />
        </button>
      </header>

      <main className="figma-theme-detail__scroll" data-node-id="7206:9599">
        <section className="figma-theme-detail__summary" data-node-id="6975:9965">
          <div className="figma-theme-detail__hero" data-node-id="6975:9969">
            {heroImage ? (
              <img src={heroImage} alt={`${theme?.title || "촬영 테마"} 대표 이미지`} />
            ) : (
              <span className="figma-theme-detail__image-empty" role="img" aria-label="등록된 테마 이미지 없음">
                이미지 준비 중
              </span>
            )}
          </div>
          <div className="figma-theme-detail__notice" data-node-id="6975:9973">
            {TEXT.popularCopy}
          </div>
          <div className="figma-theme-detail__content" data-node-id="6975:9975">
            <div className="figma-theme-detail__title-block" data-node-id="7038:14820">
              {theme?.officeName ? <p>{theme.officeName}</p> : null}
              <h2>{theme?.title || "촬영 테마"}</h2>
            </div>
            <div className="figma-theme-detail__tags" data-node-id="7038:14830">
              {tags.map((tag) => (
                <span key={tag}>{tag}</span>
              ))}
            </div>
            <button className="figma-theme-detail__like-button" type="button" aria-label={favoriteAriaLabel} aria-pressed={liked} onClick={onToggleLike} data-node-id="7038:14876">
              <HeartIcon />
              <span>{favoriteLabel}</span>
            </button>
          </div>
        </section>

        <section className="figma-theme-detail__info" data-node-id="7206:9598">
          <h3 data-node-id="7206:9596">{TEXT.detailInfo}</h3>
          {theme?.description ? (
            <p className="figma-theme-detail__description">{theme.description}</p>
          ) : (
            <p className="figma-theme-detail__description">등록된 상세 안내가 없습니다.</p>
          )}
        </section>
      </main>
      {feedbackMessage ? <div className="figma-theme-toast" role="status">{feedbackMessage}</div> : null}
      <MobileBottomTabBar activePath="/tabs/themes" />
    </section>
  );
}
export default function ShootingThemePage() {
  const searchParams = typeof window === "undefined" ? new URLSearchParams() : new URLSearchParams(window.location.search);
  const selectedScheduleId = searchParams.get("selectSchedule") || "";
  const requestedThemeId = searchParams.get("themeId") || "";
  const requestedThemeCount = Math.max(
    1,
    Math.min(20, Number(searchParams.get("requiredThemeCount")) || 1),
  );
  const scheduleThemeConfig = selectedScheduleId ? {
    scheduleId: selectedScheduleId,
    requiredPerType: requestedThemeCount,
    types: [{ id: "shoot", title: "촬영", themeCategory: "all" }],
  } : null;
  const scheduleThemeTypes = scheduleThemeConfig?.types || [];
  const scheduleThemeRequiredPerType = scheduleThemeConfig?.requiredPerType || 1;
  const isScheduleThemeSelectRequest = Boolean(selectedScheduleId && scheduleThemeConfig);
  const [scheduleThemeSelection, setScheduleThemeSelection] = useState(() => (
    scheduleThemeConfig
      ? { [scheduleThemeConfig.types[0].id]: [] }
      : {}
  ));
  const isScheduleThemeSelectMode = isScheduleThemeSelectRequest;
  const initialScheduleThemeType = scheduleThemeTypes.some((type) => type.id === searchParams.get("shootType")) ? searchParams.get("shootType") : scheduleThemeTypes[0]?.id || "";
  const [selectedCategory, setSelectedCategory] = useState(isScheduleThemeSelectMode ? scheduleThemeTypes[0]?.themeCategory || "all" : "all");
  const [selectedOfficeId, setSelectedOfficeId] = useState(0);
  const [selectedOfficeIds, setSelectedOfficeIds] = useState(() => []);
  const [draftOfficeIds, setDraftOfficeIds] = useState(() => []);
  const [selectedRegion, setSelectedRegion] = useState(REGION_FILTERS[0]);
  const [isOfficeSheetOpen, setIsOfficeSheetOpen] = useState(false);
  const [selectedPhotoProductId, setSelectedPhotoProductId] = useState(0);
  const [favoriteThemes, setFavoriteThemes] = useState([]);
  const likedMap = useMemo(() => createFavoriteMap(favoriteThemes), [favoriteThemes]);
  const [favoriteToast, setFavoriteToast] = useState("");
  const [themeSelectionCompleteOpen, setThemeSelectionCompleteOpen] = useState(false);
  const [visibleFilterKeys, setVisibleFilterKeys] = useState(() => DEFAULT_FILTER_TOKENS.map((filter) => filter.key));
  const [detailTheme, setDetailTheme] = useState(null);
  const [activeScheduleThemeTypeId, setActiveScheduleThemeTypeId] = useState(initialScheduleThemeType);
  const { pageConfig: themePageConfig } = useAppPageConfig(APP_PAGE_KEYS.themes);
  const themeQuery = useMystudioThemesV4({
    scheduleId: selectedScheduleId || undefined,
  });
  const favoriteMutation = useToggleThemeFavoriteV4();
  const themeSelectionMutation = useSubmitThemeSelectionV4();
  const activeScheduleThemeType = scheduleThemeTypes.find((type) => type.id === activeScheduleThemeTypeId) || scheduleThemeTypes[0] || { id: "", title: "촬영", themeCategory: "family" };

  const v4ThemeCatalog = themeQuery.data?.data;
  const officeList = useMemo(() => (
    Array.isArray(v4ThemeCatalog?.offices)
      ? v4ThemeCatalog.offices.map((office) => ({
        office_id: office.id,
        office_name: office.name,
      }))
      : []
  ), [v4ThemeCatalog?.offices]);
  const isOfficeListLoading = themeQuery.isLoading;
  const hasSelectedOffice = officeList.some((office) => office.office_id === selectedOfficeId);
  const hasOfficeList = officeList.length > 0 && hasSelectedOffice;
  const selectedOffice = officeList.find((office) => office.office_id === selectedOfficeId);
  const effectiveOfficeIds = useMemo(() => {
    const allowedOfficeIds = new Set(officeList.map((office) => String(office.office_id)));
    const validSelectedOfficeIds = selectedOfficeIds
      .map(String)
      .filter((officeId) => allowedOfficeIds.has(officeId));
    if (validSelectedOfficeIds.length > 0) return validSelectedOfficeIds;
    return officeList[0]?.office_id ? [String(officeList[0].office_id)] : [];
  }, [officeList, selectedOfficeIds]);

  const photoProductList = [];
  const isPhotoProductLoading = false;

  const selectedPhotoProduct = photoProductList.find((item) => item.photo_id === selectedPhotoProductId);
  const themeList = Array.isArray(v4ThemeCatalog?.items) ? v4ThemeCatalog.items : [];
  const scheduleSelectionUnavailable = Boolean(
    isScheduleThemeSelectMode
    && v4ThemeCatalog
    && !themeList.some((theme) => theme.selectable),
  );
  const isThemeListLoading = themeQuery.isLoading;

  useEffect(() => {
    if (officeList.length > 0 && !hasSelectedOffice) {
      setSelectedOfficeId(officeList[0].office_id);
      setSelectedOfficeIds([officeList[0].office_id]);
    }
  }, [hasSelectedOffice, officeList]);

  useEffect(() => {
    if (!favoriteToast) return undefined;

    const timer = window.setTimeout(() => setFavoriteToast(""), THEME_TOAST_DURATION);
    return () => window.clearTimeout(timer);
  }, [favoriteToast]);

  useEffect(() => {
    if (!themeQuery.data) return;
    setFavoriteThemes(
      themeList
        .filter((theme) => theme.isFavorite)
        .map((theme) => normalizeFavoriteTheme(createStoredTheme(normalizeV4Theme(theme)))),
    );
  }, [themeList, themeQuery.data]);

  useEffect(() => {
    if (selectedCategory === "all") {
      setSelectedPhotoProductId(0);
      return;
    }

    if (selectedCategory === "favorite") {
      setSelectedPhotoProductId(0);
      return;
    }

    const category = CATEGORIES.find((item) => item.key === selectedCategory);
    if (!category?.match || photoProductList.length === 0) return;

    const matchedProduct = photoProductList.find((item) => {
      const name = String(item.photo_name || "").toLowerCase();
      return category.match.some((keyword) => name.includes(keyword.toLowerCase()));
    });

    if (matchedProduct) {
      setSelectedPhotoProductId(matchedProduct.photo_id);
    }
  }, [photoProductList, selectedCategory]);

  const themes = useMemo(() => {
    const source = themeList.map(normalizeV4Theme);
    const officeScopedSource = effectiveOfficeIds.length > 0
      ? source.filter((theme) => theme.officeIds.some((officeId) => effectiveOfficeIds.includes(String(officeId))))
      : source;

    if (selectedCategory === "favorite") {
      return effectiveOfficeIds.length > 0
        ? favoriteThemes.filter((theme) => theme.officeIds?.some((officeId) => effectiveOfficeIds.includes(String(officeId))))
        : favoriteThemes;
    }

    const filteredThemes = officeScopedSource.filter((theme) => matchesCategory(theme, selectedCategory));

    if (!isScheduleThemeSelectMode) {
      return filteredThemes;
    }

    return [...filteredThemes].sort((a, b) => Number(Boolean(likedMap[b.id])) - Number(Boolean(likedMap[a.id])));
  }, [
    favoriteThemes,
    isScheduleThemeSelectMode,
    likedMap,
    selectedCategory,
    effectiveOfficeIds,
    themeList,
  ]);

  const officeOptions = useMemo(() => {
    return officeList.map(normalizeOfficeOption);
  }, [
    officeList,
  ]);

  const selectedOfficeNames = officeOptions.filter((office) => effectiveOfficeIds.includes(office.id)).map((office) => office.name);
  const officeName = selectedOfficeNames.length > 1 ? `${selectedOfficeNames[0]} ${OFFICE_SHEET_TEXT.more} ${selectedOfficeNames.length - 1}` : selectedOfficeNames[0] || selectedOffice?.office_name || TEXT.allOffice;
  const isLoading = isOfficeListLoading || isPhotoProductLoading || isThemeListLoading;
  const visibleFilters = DEFAULT_FILTER_TOKENS.filter((filter) => visibleFilterKeys.includes(filter.key));
  const visibleThemeSections = useMemo(
    () => getVisibleSections(themePageConfig),
    [themePageConfig],
  );

  useEffect(() => {
    if (!isScheduleThemeSelectMode) return;
    setSelectedCategory(activeScheduleThemeType.themeCategory);
  }, [activeScheduleThemeType.themeCategory, isScheduleThemeSelectMode]);

  useEffect(() => {
    if (!requestedThemeId || detailTheme || themeList.length === 0) return;
    const requestedTheme = themeList.find(
      (theme) => String(theme.id) === String(requestedThemeId),
    );
    if (!requestedTheme) return;
    setDetailTheme(normalizeV4Theme(requestedTheme));
    const nextUrl = `${window.location.pathname}${window.location.search}#theme-detail`;
    window.history.replaceState(
      { screen: "theme-detail", themeId: requestedThemeId },
      "",
      nextUrl,
    );
  }, [detailTheme, requestedThemeId, themeList]);

  useEffect(() => {
    const syncThemeDetailHash = () => {
      if (window.location.hash !== "#theme-detail") {
        setDetailTheme(null);
      }
    };

    window.addEventListener("popstate", syncThemeDetailHash);
    window.addEventListener("hashchange", syncThemeDetailHash);
    return () => {
      window.removeEventListener("popstate", syncThemeDetailHash);
      window.removeEventListener("hashchange", syncThemeDetailHash);
    };
  }, []);

  useEffect(() => {
    if (!requestedThemeId && window.location.hash === "#theme-detail" && !detailTheme && themes.length > 0) {
      setDetailTheme(themes[0]);
    }
  }, [detailTheme, requestedThemeId, themes]);

  const toggleLike = async (theme) => {
    const themeId = String(theme.id);
    const alreadyLiked = favoriteThemes.some((item) => String(item.id) === themeId);
    const nextThemes = alreadyLiked
      ? favoriteThemes.filter((item) => String(item.id) !== themeId)
      : [normalizeFavoriteTheme(createStoredTheme(theme)), ...favoriteThemes.filter((item) => String(item.id) !== themeId)];

    setFavoriteThemes(nextThemes);
    setFavoriteToast(alreadyLiked ? TEXT.favoriteRemoved : TEXT.favoriteAdded);

    try {
      await favoriteMutation.mutateAsync({ themeId, favorite: !alreadyLiked });
    } catch (error) {
      setFavoriteThemes(favoriteThemes);
      setFavoriteToast(error?.message || "찜하기를 저장하지 못했어요. 다시 시도해 주세요.");
    }
  };

  const toggleScheduleTheme = (theme) => {
    setScheduleThemeSelection((current) => {
      const normalizedSelection = scheduleThemeTypes.reduce((selection, type) => ({
        ...selection,
        [type.id]: Array.isArray(current?.[type.id]) ? current[type.id] : [],
      }), {});
      const selectedItems = normalizedSelection[activeScheduleThemeType.id] || [];
      const alreadySelected = selectedItems.some((item) => String(item.id) === String(theme.id));

      if (alreadySelected) {
        return {
          ...normalizedSelection,
          [activeScheduleThemeType.id]: selectedItems.filter((item) => String(item.id) !== String(theme.id)),
        };
      }

      if (selectedItems.length >= scheduleThemeRequiredPerType) {
        window.alert(`${activeScheduleThemeType.title}\uC740 ${scheduleThemeRequiredPerType}\uAC1C\uAE4C\uC9C0 \uC120\uD0DD\uD560 \uC218 \uC788\uC5B4\uC694.`);
        return normalizedSelection;
      }

      return {
        ...normalizedSelection,
        [activeScheduleThemeType.id]: [...selectedItems, createStoredTheme(theme)],
      };
    });
  };

  const completeScheduleThemeSelection = async () => {
    const complete = scheduleThemeTypes.every(
      (type) => (scheduleThemeSelection[type.id]?.length || 0) === scheduleThemeRequiredPerType,
    );
    if (!complete) return;

    try {
      const themeIds = scheduleThemeTypes.flatMap(
        (type) => (scheduleThemeSelection[type.id] || []).map((theme) => String(theme.id)),
      );
      await themeSelectionMutation.mutateAsync({
        scheduleId: selectedScheduleId,
        input: { themeIds },
      });
      setThemeSelectionCompleteOpen(true);
    } catch (error) {
      setFavoriteToast(error?.message || "테마 선택을 저장하지 못했어요. 다시 시도해 주세요.");
    }
  };

  const confirmScheduleThemeSelection = () => {
    const params = new URLSearchParams();
    params.set("category", "upcoming");
    params.set("refresh", "theme-selected");
    window.location.href = `/tabs/schedules?${params.toString()}`;
  };

  const removeFilter = (filterKey) => {
    setVisibleFilterKeys((current) => current.filter((key) => key !== filterKey));
  };

  const openThemeDetail = (theme) => {
    setDetailTheme(theme);
    const nextUrl = `${window.location.pathname}${window.location.search}#theme-detail`;
    window.history.pushState({ screen: "theme-detail", themeId: theme.id }, "", nextUrl);
  };

  const closeThemeDetail = () => {
    setDetailTheme(null);
    const nextParams = new URLSearchParams(window.location.search);
    nextParams.delete("themeId");
    const nextQuery = nextParams.toString();
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ""}`,
    );
  };

  const openOfficeSheet = () => {
    if (officeOptions.length <= 1) return;
    setDraftOfficeIds(effectiveOfficeIds.length > 0 ? effectiveOfficeIds : getDefaultOfficeIds(officeOptions, selectedOfficeId));
    setIsOfficeSheetOpen(true);
  };

  const toggleDraftOffice = (officeId) => {
    setDraftOfficeIds((current) => (current.includes(officeId) ? current.filter((id) => id !== officeId) : [...current, officeId]));
  };

  const applyOfficeSelection = () => {
    setSelectedOfficeIds(draftOfficeIds);
    setSelectedOfficeId(draftOfficeIds[0] > 0 ? draftOfficeIds[0] : 0);
    setIsOfficeSheetOpen(false);
  };

  if (themePageConfig.page.enabled === false) {
    return (
      <ConfiguredPageDisabledState
        pageConfig={themePageConfig}
        activePath="/tabs/themes"
      />
    );
  }

  if (scheduleSelectionUnavailable) {
    return (
      <section className="figma-theme-page mobile-app-frame" data-node-id="6743:theme-page">
        <MobileStatusBar />
        <MobileTopHeader />
        <main className="figma-theme-scroll">
          <MystudioDataState
            type="empty"
            title="테마 선택 연동을 준비하고 있어요"
            description="ERP의 테마 선택 가능 항목과 필수 개수 규칙이 연결된 뒤 이용할 수 있어요."
          />
        </main>
        <MobileBottomTabBar activePath="/tabs/themes" />
      </section>
    );
  }

  if (detailTheme) {
    return (
      <ThemeDetailPage
        theme={detailTheme}
        liked={Boolean(likedMap[detailTheme.id] ?? detailTheme.liked)}
        feedbackMessage={favoriteToast}
        onBack={closeThemeDetail}
        onToggleLike={() => toggleLike(detailTheme)}
      />
    );
  }

  const renderThemeSection = (section) => {
    const sectionProps = getSectionProps(section);

    if (section.key === "selection") {
      if (!isScheduleThemeSelectMode) return null;
      return (
        <ScheduleThemeSelectionPanel
          key={section.key}
          {...sectionProps}
          activeTypeId={activeScheduleThemeTypeId}
          selection={scheduleThemeSelection}
          selectTypes={scheduleThemeTypes}
          requiredPerType={scheduleThemeRequiredPerType}
          submitting={themeSelectionMutation.isPending}
          onSelectType={setActiveScheduleThemeTypeId}
          onComplete={completeScheduleThemeSelection}
        />
      );
    }

    if (section.key === "filters") {
      return (
        <section className="figma-theme-config-section figma-theme-config-section--filters" key={section.key}>
          {sectionProps.title ? <h2>{sectionProps.title}</h2> : null}
          {sectionProps.description ? <p>{sectionProps.description}</p> : null}
          <ThemeFilterPanel
            selectedCategory={selectedCategory}
            onSelectCategory={setSelectedCategory}
            officeName={officeName}
            onOpenOfficeSheet={openOfficeSheet}
            filters={visibleFilters}
            onRemoveFilter={removeFilter}
            selectionMode={isScheduleThemeSelectMode}
            fixedOffice={officeOptions.length <= 1}
          />
          {!isLoading && !themeQuery.error && v4ThemeCatalog?.availability !== "unavailable" ? (
            <p className="figma-theme-result-summary">
              <strong>{officeName}</strong>
              <span>촬영테마 {themes.length}개</span>
            </p>
          ) : null}
        </section>
      );
    }

    if (section.key === "theme_grid") {
      return (
        <section className="figma-theme-config-section figma-theme-config-section--grid" key={section.key}>
          {sectionProps.title ? <h2>{sectionProps.title}</h2> : null}
          {sectionProps.description ? <p>{sectionProps.description}</p> : null}
          {isLoading ? <MystudioDataState type="loading" /> : null}
          {!isLoading && (!themeQuery.configured || themeQuery.error) ? (
            <MystudioDataState
              type="error"
              error={themeQuery.error}
              title={!themeQuery.configured ? "촬영테마 API가 연결되지 않았어요" : undefined}
              description={!themeQuery.configured ? "MyStudio API 연결 주소를 확인해 주세요." : undefined}
              onRetry={themeQuery.configured ? themeQuery.refetch : undefined}
            />
          ) : null}
          {!isLoading && !themeQuery.error && v4ThemeCatalog?.availability === "unavailable" ? (
            <MystudioDataState
              type="empty"
              title="촬영테마 연동을 준비하고 있어요"
              description={v4ThemeCatalog.reasonCode || "스튜디오에서 테마 정보를 연동하면 이곳에 표시됩니다."}
            />
          ) : null}
          {!isLoading && themeQuery.configured && !themeQuery.error && v4ThemeCatalog?.availability !== "unavailable" ? (
            <div className="figma-theme-grid" data-node-id="7038:14553">
            {themes.length > 0 ? (
              themes.map((theme) => (
                <ThemeCard
                  key={theme.id}
                  theme={theme}
                  liked={isScheduleThemeSelectMode ? Boolean(scheduleThemeSelection[activeScheduleThemeType.id]?.some((item) => String(item.id) === String(theme.id))) : Boolean(likedMap[theme.id] ?? theme.liked)}
                  selected={Boolean(scheduleThemeSelection[activeScheduleThemeType.id]?.some((item) => String(item.id) === String(theme.id)))}
                  selectionMode={isScheduleThemeSelectMode}
                  selectionDisabled={(scheduleThemeSelection[activeScheduleThemeType.id]?.length || 0) >= scheduleThemeRequiredPerType}
                  onToggleLike={() => (isScheduleThemeSelectMode ? toggleScheduleTheme(theme) : toggleLike(theme))}
                  onOpen={() => (isScheduleThemeSelectMode ? toggleScheduleTheme(theme) : openThemeDetail(theme))}
                />
              ))
            ) : (
              <ThemeEmptyState favorite={selectedCategory === "favorite"} officeName={officeName} />
            )}
            </div>
          ) : null}
        </section>
      );
    }

    return null;
  };

  return (
    <section className="figma-theme-page mobile-app-frame" data-node-id="6743:theme-page">
      <MobileStatusBar />
      <MobileTopHeader />
      <main className="figma-theme-scroll">
        <ConfiguredPageIntro pageConfig={themePageConfig} />
        {visibleThemeSections.map(renderThemeSection)}
      </main>
      {favoriteToast ? <div className="figma-theme-toast" role="status">{favoriteToast}</div> : null}
      <OfficeLocationSheet
        open={isOfficeSheetOpen}
        officeOptions={officeOptions}
        draftOfficeIds={draftOfficeIds}
        selectedRegion={selectedRegion}
        showRegionFilter
        onSelectRegion={setSelectedRegion}
        onToggleOffice={toggleDraftOffice}
        onRemoveOffice={(officeId) => setDraftOfficeIds((current) => current.filter((id) => id !== officeId))}
        onReset={() => setDraftOfficeIds([])}
        onApply={applyOfficeSelection}
        onClose={() => setIsOfficeSheetOpen(false)}
      />
      {themeSelectionCompleteOpen ? (
        <ThemeSelectionCompleteDialog onConfirm={confirmScheduleThemeSelection} />
      ) : null}
      <MobileBottomTabBar activePath={window.location.pathname} />
    </section>
  );
}
